/**
 * Browser tracing.
 *
 * DEE-27's acceptance criterion is `traceparent` **propagation**, not
 * export — a browser span joined to the API's trace is what makes a slow
 * page attributable. That works whether or not anything leaves the
 * browser, which matters because today nothing can:
 *
 * production CSP is `connect-src 'self'`, `apps/web/nginx.conf`
 * deliberately proxies nothing, and the collector is a sidecar beside the
 * API task rather than something publicly reachable. So export ships
 * behind `VITE_OTEL_ENABLED`, **default off**, and the recommended fix — a
 * `POST /api/v1/telemetry/traces` forwarder on FastAPI, same origin, CSP
 * unchanged — is an infra decision, not a coding task.
 *
 * Note also that `VITE_OTEL_ENABLED` is this app's first `VITE_*`
 * variable, which ends the web image's environment-agnostic property:
 * Vite inlines at build time, so one image can no longer run in every
 * environment. Flagged on DEE-27.
 */

import { OTLPTraceExporter } from "@opentelemetry/exporter-trace-otlp-http";
import { registerInstrumentations } from "@opentelemetry/instrumentation";
import { FetchInstrumentation } from "@opentelemetry/instrumentation-fetch";
import {
  BatchSpanProcessor,
  StackContextManager,
  WebTracerProvider,
} from "@opentelemetry/sdk-trace-web";

/** Same string in development and production — the Vite proxy in dev, the
 *  forwarder in prod. Never a cross-origin collector URL. */
const EXPORTER_URL = "/v1/traces";

export interface TelemetryOptions {
  /** Off unless the build sets it. */
  exportEnabled?: boolean;
  /** Origins the `traceparent` header may be attached to. Same-origin
   *  only: sending our trace context to a third party would leak the
   *  shape of internal requests. */
  propagateTo?: (string | RegExp)[];
}

export function startTelemetry(options: TelemetryOptions = {}): void {
  const exportEnabled = options.exportEnabled ?? import.meta.env.VITE_OTEL_ENABLED === "true";

  const provider = new WebTracerProvider({
    spanProcessors: exportEnabled
      ? [new BatchSpanProcessor(new OTLPTraceExporter({ url: EXPORTER_URL }))]
      : [],
  });

  // `StackContextManager`, not `ZoneContextManager`: the zone package
  // drags in zone.js, an Angular-shaped dependency, and React's rendering
  // does not need it.
  provider.register({ contextManager: new StackContextManager() });

  registerInstrumentations({
    instrumentations: [
      new FetchInstrumentation({
        propagateTraceHeaderCorsUrls: options.propagateTo ?? [/^\/(api|auth)\//],
        // The exporter's own POST must not be traced, or it produces a
        // span, which produces an export, which produces a span. The loop
        // is not subtle once it starts and this is the only thing stopping
        // it.
        ignoreUrls: [EXPORTER_URL],
      }),
    ],
  });
}
